<!--
 富文本编辑器插件使用的是wangEditor V5版本
 wangEditor官方网址：https://www.wangeditor.com/
 wangEditor github: https://github.com/wangeditor-team/wangEditor
 -->
<template>
  <div v-if="!defaultConfig.editorConfig.readOnly" ref="editorWrapper" class="comp-w-editor">
    <toolbar :editor="editorRef" :defaultConfig="toolbarConfig" :mode="mode" class="editor-tool" />
    <editor ref="editorBody" v-model="valueHtml" :style="editorStyle" :defaultConfig="editorConfig" mode="simple" @onCreated="handleCreated" class="editor" @onBlur="handleBlur" @vue:beforeUnmount="onEditorBeforeUnmount" />
    <jw-row v-if="defaultConfig.needSave" class="footer" justify="end">
      <jw-space>
        <jw-button @click="handleCancel" type="outline">{{ $t('取消') }}</jw-button>
        <jw-button @click="handleSave" type="primary">{{ $t('确定') }}</jw-button>
      </jw-space>
    </jw-row>
    <jw-spin v-if="formatLoading" class="editor-loading" loading style="width: 100%; height: 100%" :tip="$t('AI排版中...')">
      <!-- 必须有个空div 否则loading不生成遮罩 -->
      <div />
    </jw-spin>
  </div>
  <div v-else-if="valueHtml" v-html="valueHtml" class="editor-disabled-view"></div>
  <div v-else class="editor-disabled-view">
    <div class="holder-text">{{ editorConfig.placeholder }}</div>
  </div>

  <org-persion-list v-if="showBox" :keyWords="keyWords" :position="listPosition" @close="handelClose" @change="handelChange"></org-persion-list>
</template>

<script setup>
// let setRange = ''; //区域对象
import { reactive, ref, onBeforeUnmount, shallowRef, computed, watch, nextTick } from 'vue';
import { getToken } from '@/utils/auth';
import '@wangeditor/editor/dist/css/style.css'; // 引入 css
import { Editor, Toolbar } from '@wangeditor/editor-for-vue';
import registerModule from './js/module';
import { Message } from '@arco-design/web-vue';
import { useI18n } from 'vue-i18n';
import { aiService } from '@/api';
import DOMPurify from 'dompurify';
import { useGrayscaleStore } from '@/store';
import { useRichTextAiFormatInject } from '@/hooks/rich-text-ai';

const { setLoading, resetLoading } = useRichTextAiFormatInject();
const { grayList } = useGrayscaleStore();
const { t } = useI18n();
registerModule();
const token = getToken() || null;
const emits = defineEmits(['update:modelValue', 'blur', 'full-screen-change']);
const mode = 'simple';
const props = defineProps({
  modelValue: {
    type: String
  },
  configs: {
    type: Object,
    default: () => {
      return {};
    }
  },
  isTask: {
    /* 是否是阶段流程任务配置中的富文本 */
    type: Boolean,
    default: false
  },
  showAiMenu: {
    type: Boolean,
    default: true
  }
});

const formatLoading = ref(false);
watch(formatLoading, (newVal) => {
  if (newVal) {
    setLoading();
  } else {
    resetLoading();
  }
});

const showBox = ref(false);
const listPosition = ref({
  left: 0,
  top: 0
});
const editorBody = ref(null);
const editorWrapper = ref(null); // 编辑器容器的 ref
const isFullScreen = ref(false); // 全屏状态标记
const originalParent = ref(null); // 保存原始父节点
const placeholder = ref(null); // 占位元素
let fullScreenObserver = null; // MutationObserver 实例
// 默认配置
const defaultConfig = reactive({
  needSave: false,
  height: 200,
  toolbarConfig: {
    toolbarKeys: [
      // 'saveBtn',
      'headerSelect', // 正文、标题选择
      // 'blockquote', // 引用
      // '|', // 分割线
      'bold', // 加粗
      // 'underline', // 下划线
      'italic', // 斜体
      // {
      //   key: 'group-more-style',
      //   title: '更多',
      //   iconSvg: '<svg viewBox="0 0 1024 1024"><path d="M204.8 505.6m-76.8 0a76.8 76.8 0 1 0 153.6 0 76.8 76.8 0 1 0-153.6 0Z"></path><path d="M505.6 505.6m-76.8 0a76.8 76.8 0 1 0 153.6 0 76.8 76.8 0 1 0-153.6 0Z"></path><path d="M806.4 505.6m-76.8 0a76.8 76.8 0 1 0 153.6 0 76.8 76.8 0 1 0-153.6 0Z"></path></svg>',
      //   menuKeys: [
      //     'through', // 删除线
      //     'code', // 行内代码
      //     'sup', // 上标
      //     'sub', // 下标
      //     'clearStyle' // 清除格式
      //   ]
      // },
      'color', // 文字颜色
      'bgColor', // 背景色
      // '|', // 分割线
      // 'fontSize', // 字号
      // 'fontFamily', // 字体
      // 'lineHeight', // 行高
      // '|', // 分割线
      'bulletedList', // 无序列表
      'numberedList', // 有序列表
      // 'todo', // 待办
      // {
      //   key: 'group-justify',
      //   title: '对齐',
      //   iconSvg: '<svg viewBox="0 0 1024 1024"><path d="M768 793.6v102.4H51.2v-102.4h716.8z m204.8-230.4v102.4H51.2v-102.4h921.6z m-204.8-230.4v102.4H51.2v-102.4h716.8zM972.8 102.4v102.4H51.2V102.4h921.6z"></path></svg>',
      //   menuKeys: [
      //     'justifyLeft', // 左对齐
      //     'justifyRight', // 右对齐
      //     'justifyCenter', // 居中对齐
      //     'justifyJustify' // 两端对齐
      //   ]
      // },
      // {
      //   key: 'group-indent',
      //   title: '缩进',
      //   iconSvg: '<svg viewBox="0 0 1024 1024"><path d="M0 64h1024v128H0z m384 192h640v128H384z m0 192h640v128H384z m0 192h640v128H384zM0 832h1024v128H0z m0-128V320l256 192z"></path></svg>',
      //   menuKeys: [
      //     'indent', // 增加缩进
      //     'delIndent' // 减少缩进
      //   ]
      // },
      // '|', // 分割线
      'emotion', // 表情
      // 'insertLink', // 插入链接
      {
        key: 'group-image',
        title: '图片',
        iconSvg: '<svg viewBox="0 0 1024 1024"><path d="M959.877 128l0.123 0.123v767.775l-0.123 0.122H64.102l-0.122-0.122V128.123l0.122-0.123h895.775zM960 64H64C28.795 64 0 92.795 0 128v768c0 35.205 28.795 64 64 64h896c35.205 0 64-28.795 64-64V128c0-35.205-28.795-64-64-64zM832 288.01c0 53.023-42.988 96.01-96.01 96.01s-96.01-42.987-96.01-96.01S682.967 192 735.99 192 832 234.988 832 288.01zM896 832H128V704l224.01-384 256 320h64l224.01-192z"></path></svg>',
        menuKeys: [
          // 'insertImage', // 网络图片
          'uploadImage' // 上传图片
        ]
      },
      // {
      //   key: 'group-video',
      //   title: '视频',
      //   iconSvg: '<svg viewBox="0 0 1024 1024"><path d="M981.184 160.096C837.568 139.456 678.848 128 512 128S186.432 139.456 42.816 160.096C15.296 267.808 0 386.848 0 512s15.264 244.16 42.816 351.904C186.464 884.544 345.152 896 512 896s325.568-11.456 469.184-32.096C1008.704 756.192 1024 637.152 1024 512s-15.264-244.16-42.816-351.904zM384 704V320l320 192-320 192z"></path></svg>',
      //   menuKeys: [
      //     'insertVideo', // 插入视频
      //     'uploadVideo' // 上传视频
      //   ]
      // },
      // 'insertTable', // 插入表格
      // 'codeBlock', // 代码块
      // 'divider', // 分割线
      // '|', // 分割线
      // 'undo', // 撤销
      // 'redo', // 重做
      // '|', // 分割线
      'fullScreen' // 全屏
    ]
  },
  editorConfig: {
    placeholder: '请输入内容...',
    readOnly: false // 是否只读
  },
  ...props.configs
});

const editorStyle = computed(() => {
  return {
    height: defaultConfig.height + 'px'
  };
});

// 编辑器实例，必须用 shallowRef
const editorRef = shallowRef();
const toolbarConfig = computed(() => ({
  ...defaultConfig.toolbarConfig,
  toolbarKeys: [
    ...defaultConfig.toolbarConfig.toolbarKeys,
    props.showAiMenu && grayList?.['Layout:aiFormat'] ? 'aiMenu' : undefined // 智能菜单
  ].filter(Boolean) // 过滤掉 undefined
}));
const editorConfig = {
  ...defaultConfig.editorConfig,
  autoFocus: props.isTask ? false : true,
  MENU_CONF: {
    // 菜单配置
    uploadImage: {
      // 上传图片配置
      server: '/api/uploadFileRichText',
      // server: '/api/uploadRichTextMultiPartFile',
      fieldName: 'file',
      // 单个文件的最大体积限制，默认为 2M
      maxFileSize: 100 * 1024 * 1024, // 100M
      // 最多可上传几个文件，默认为 100
      maxNumberOfFiles: 10,
      // 选择文件时的类型限制，默认为 ['image/*'] 。如不想限制，则设置为 []
      allowedFileTypes: ['image/*'],
      // 自定义增加 http  header
      headers: {
        authorization: `Bearer ${token}`
      },
      // 超时时间，默认为 10 秒
      timeout: 60 * 1000, // 60 秒
      // 上传错误，或者触发 timeout 超时
      onError(file, err, res) {
        console.log(`${file.name} 上传出错`, err, res);
        Message.error(t(`${file.name} 上传出错`));
      }
    }
  }
};

// 内容 HTML
const valueHtml = ref(props.modelValue);
// 内容 text
const keyWords = ref(null);
const updateKeyWords = (start) => {
  const end = JSON.parse(JSON.stringify(editorRef.value.selection));
  const editorData = editorRef.value.children;
  const endPath1 = end && end.focus.path[0];
  const endPath2 = end && end.focus.path[1];
  if (start.anchor.offset === end.focus.offset) {
    // 删除@符关闭弹窗
    handelClose();
    return;
  }
  // 解析光标所在处的@符后的文字
  if (editorData.length) {
    if (editorData[endPath1] && editorData[endPath1].children?.length) {
      const target = editorData[endPath1].children[endPath2];
      const list = target?.text.substring(start.anchor.offset, end.focus.offset).split('@');
      keyWords.value = list.at(-1);
    }
  }
};

// 计算光标距离浏览器窗口上下左右的距离
const getCursorCoordinates = () => {
  const getSelectionPosition = editorRef.value.getSelectionPosition(); //选区相对于富文本的位置
  const getEditableContainerPosition = editorRef.value.getEditableContainer().getBoundingClientRect(); //富文本相对于body的位置
  //由这俩个位置从而算出光标选区相对body的位置
  //   console.log("getSelectionPosition----",getSelectionPosition)
  //   console.log("getEditableContainerPosition----",getEditableContainerPosition)
  const rengeLeft = getSelectionPosition?.left ? Number(getSelectionPosition?.left?.split('px')[0]) : '';
  const rangeTop = getSelectionPosition?.top ? Number(getSelectionPosition?.top?.split('px')[0]) : '';
  const rengeRight = getSelectionPosition?.right ? Number(getSelectionPosition?.right?.split('px')[0]) : '';
  const rangeBottom = getSelectionPosition?.bottom ? Number(getSelectionPosition?.bottom?.split('px')[0]) : '';

  const containerLeft = getEditableContainerPosition.left;
  const containerTop = getEditableContainerPosition.top;
  let left = 0;
  let top = 0;
  if (rengeLeft) {
    left = rengeLeft + containerLeft - 15; //(富文本中元素间有marge 需要减去) //距离body左侧距离
  }
  if (rangeTop) {
    top = rangeTop + containerTop - 15; //(富文本中元素间有marge 需要减去)//距离body上侧距离
  }
  if (rengeRight) {
    left = containerLeft + (getEditableContainerPosition.width - rengeRight);
  }
  if (rangeBottom) {
    top = containerTop + (getEditableContainerPosition.height - rangeBottom);
  }
  return {
    left: left, //富文本中元素间有marge 需要减去
    top: top //富文本中元素间有marge 需要减去
  };
  //   var x = 0,
  //     y = 0;
  //   var sel = window.getSelection();
  //   if (sel.rangeCount) {
  //     var range = sel.getRangeAt(0).cloneRange();
  //     var getRects = range.getClientRects()?.[0];
  //     console.log('setRange----',setRange)
  //     console.log('getRects----',getRects)
  //     if (!setRange || (setRange && getRects && (setRange.left != getRects.left || setRange.top != getRects.top || setRange.height != getRects.height || setRange.width != getRects.width))) {
  //       setRange = getRects;
  //     }
  //     if(!getRects){
  //         // const getSelectionPosition =editorRef.value.getSelectionPosition() //选区相对于富文本的位置
  //         // const getEditableContainerPosition =editorRef.value.getEditableContainer().getBoundingClientRect() //编辑区域 DOM 元素的定位
  //         //  x=getSelectionPosition.left
  //         // insertedInput();
  //         // editorRef.value.focus();
  //         // console.log("更新后",window.getSelection().getRangeAt(0).cloneRange().getClientRects()?.[0])
  //         // getRects=window.getSelection().getRangeAt(0).cloneRange().getClientRects()?.[0];
  //     }else{
  //     if (setRange) {
  //       x = setRange.left;
  //       y = setRange.top;
  //     }
  //     }

  //   }

  //   // 计算光标位置距离窗口可视区域的距离
  //   var cursorTopFromWindow = y;
  //   var cursorBottomFromWindow = window.innerHeight - y - setRange.height;
  //   var cursorLeftFromWindow = x;
  //   var cursorRightFromWindow = window.innerWidth - x - setRange.width;

  //   return {
  //     top: cursorTopFromWindow,
  //     bottom: cursorBottomFromWindow,
  //     left: cursorLeftFromWindow,
  //     right: cursorRightFromWindow
  //   };
};

let start;
// 监听@按下 计算弹窗位置
const keyLintener = (e) => {
  const { keyCode } = e;
  // 英文code是 50， 判断是否按住shift + @键
  // 中文输入法下标点符号keyCode都是一样的：229，推荐使用event.code或event.key作为@的判断。
  const isCode = ((keyCode === 229 && e.key === '@') || (keyCode === 229 && e.code === 'Digit2') || keyCode === 50) && e.shiftKey;
  if (isCode && editorBody.value) {
    listPosition.value = getCursorCoordinates();

    if (!showBox.value) {
      start = JSON.parse(JSON.stringify(editorRef.value.selection));
    }
    showBox.value = true;
  }
};

// 根据用户id获取节点路径
const getUserPath = (userId) => {
  const data = editorRef.value.children;
  if (!data?.length) return;
  let path = [0, 0];
  data.forEach((item, index) => {
    if (item?.children && item?.children.length) {
      item.children.forEach((child, childIndex) => {
        if (child.type === 'mention' && child.userId === userId) {
          path = [index, childIndex + 1]; // 这里是因为插入@人员后会多一个‘’
        }
      });
    }
  });
  return path;
};

// 替换并插入一段html
const repAndInsText = (userInfo) => {
  const end = editorRef.value.selection;
  const selected = {
    anchor: { path: start.anchor.path, offset: start.anchor.offset },
    focus: { path: end.focus.path, offset: end.focus.offset }
  };
  editorRef.value.select(selected);
  const node = { type: 'mention', userId: userInfo?.id, value: `@${userInfo?.userName}`, children: [{ text: 'simple text' }] };
  editorRef.value.insertNode(node);
  editorRef.value.deselect();
  const path = getUserPath(userInfo?.id);
  const sel = {
    anchor: { path: path, offset: 0 },
    focus: { path: path, offset: 0 }
  };
  editorRef.value.select(sel);
};

/**
 * 设置HTML内容 尝试3次
 * 解决偶发报错
 * https://github.com/ianstormtaylor/slate/issues/4240#issuecomment-920834086
 * https://github.com/wangeditor-team/wangEditor/issues/5038
 * @param html 要设置的 HTML 内容
 */
const setHtml = (html) => {
  const trySet = (res, rej, timer = 3) => {
    if (timer <= 0) {
      rej(new Error(t('AI格式整理失败')));
      return;
    }
    try {
      editorRef.value.selectAll();
      editorRef.value.deleteFragment();
      editorRef.value.dangerouslyInsertHtml(html);
      Message.success(t('格式整理成功'));
      res(html);
    } catch {
      setTimeout(() => {
        if (!editorRef.value) return;
        trySet(res, rej, timer - 1);
      });
    }
  };
  return new Promise((res, rej) => {
    trySet(res, rej);
  });
};

/**
 * AI格式整理
 */
const aiMenuListener = async () => {
  if (!editorRef.value) return;
  // 记录禁用状态
  const isDisabled = editorRef.value.isDisabled();
  try {
    formatLoading.value = true;
    const content = editorRef.value.getHtml();
    editorRef.value.disable();
    const res = await aiService.format(content);
    if (!editorRef.value) return;
    if (res.error) {
      Message.error(res.error?.msg || t('AI格式整理失败'));
      return;
    }
    if (res.result?.content) {
      // 对 HTML 进行清理，防止 XSS 攻击
      const html = DOMPurify.sanitize(res.result.content);
      // 先启用 否者禁用状态下setHtml控制台会报错
      editorRef.value.enable();
      await setHtml(html);
    }
  } catch (e) {
    console.log(e);
    Message.error(e.message || e.statusText || t('AI格式整理失败'));
  } finally {
    // 恢复禁用状态
    // 延迟恢复 否者setHtml和enable同时调用控制台会报错
    setTimeout(() => {
      formatLoading.value = false;
      if (!editorRef.value) return;
      if (isDisabled) {
        editorRef.value.disable();
      } else {
        editorRef.value.enable();
      }
    });
  }
};

// 监听全屏状态变化
const observeFullScreen = () => {
  if (!editorWrapper.value) return;

  fullScreenObserver = new MutationObserver(() => {
    // 检查 editorWrapper 自身是否有 w-e-full-screen-container 类
    const newIsFullScreen = editorWrapper.value?.classList.contains('w-e-full-screen-container');

    if (newIsFullScreen !== isFullScreen.value) {
      isFullScreen.value = newIsFullScreen;
      emits('full-screen-change', isFullScreen.value);

      if (isFullScreen.value) {
        // 进入全屏：将编辑器移动到 body
        moveToBody();
      } else {
        // 退出全屏：将编辑器移回原位置
        moveBackToOriginal();
      }
    }
  });

  fullScreenObserver.observe(editorWrapper.value, {
    attributes: true,
    attributeFilter: ['class']
  });
};

// 将编辑器移动到 body
const moveToBody = () => {
  if (!editorWrapper.value) return;

  // 保存原始父节点
  originalParent.value = editorWrapper.value.parentNode;

  // 创建占位元素，保持原位置
  placeholder.value = document.createElement('div');
  placeholder.value.style.display = 'none';
  originalParent.value.insertBefore(placeholder.value, editorWrapper.value);

  // 移动到 body
  document.body.appendChild(editorWrapper.value);
};

// 将编辑器移回原位置
const moveBackToOriginal = () => {
  if (!editorWrapper.value || !originalParent.value || !placeholder.value) return;

  // 移回原位置
  originalParent.value.insertBefore(editorWrapper.value, placeholder.value);

  // 移除占位元素
  placeholder.value.remove();
  placeholder.value = null;
  originalParent.value = null;
};

// 组件销毁时，也及时销毁编辑器
onBeforeUnmount(() => {
  if (editorBody.value && editorBody.value?.$el) {
    editorBody.value.$el.removeEventListener('keydown', keyLintener);
    editorRef.value.off('aiMenu', aiMenuListener);
  }
  editorRef.value?.destroy();
  editorRef.value = null;
  formatLoading.value = false;

  // 断开 MutationObserver
  if (fullScreenObserver) {
    fullScreenObserver.disconnect();
    fullScreenObserver = null;
  }

  // 如果还在全屏状态，先移回原位置
  if (isFullScreen.value) {
    moveBackToOriginal();
  }

  const editor = editorRef.value;
  if (editor == null) return;
  editor?.destroy();
});

const handleCreated = (editor) => {
  editorRef.value = editor; // 记录 editor 实例，重要！
  if (editorBody.value && editorBody.value?.$el) {
    //页面卸载已移除监听 避免内存泄露
    editorBody.value.$el.addEventListener('keydown', keyLintener);
    editorRef.value.on('aiMenu', aiMenuListener);
  }

  // 开始监听全屏状态
  nextTick(() => {
    observeFullScreen();
  });
};
// 聚焦到编辑器
const focus = () => {
  editorRef.value?.focus?.();
};

const getFullScreenButton = async () => {
  await nextTick();
  let button = editorWrapper.value?.querySelector(".w-e-bar-item button[data-menu-key='fullScreen']");

  if (!button) {
    await nextTick();
    button = editorWrapper.value?.querySelector(".w-e-bar-item button[data-menu-key='fullScreen']");
  }

  return button;
};

const toggleFullScreen = async (force) => {
  const button = await getFullScreenButton();
  if (!button) return false;

  const nextFullScreenStatus = typeof force === 'boolean' ? force : !isFullScreen.value;
  if (nextFullScreenStatus === isFullScreen.value) return true;

  button.click();
  return true;
};

const openFullScreen = () => {
  return toggleFullScreen(true);
};

const closeFullScreen = () => {
  return toggleFullScreen(false);
};

// 阶段流程任务失焦
const handleBlur = () => {
  emits('blur', valueHtml.value);
};

// 点击确定
const handleSave = () => {
  emits('save', valueHtml.value);
};
// 点击取消
const handleCancel = () => {
  emits('cancel');
};

// 关闭组织人员列表弹窗
const handelClose = () => {
  keyWords.value = null; // 重置组织人员查询关键词
  showBox.value = false;
  //2025年03月18日20:02:27zl将setTimeout0设置为nextTick
  nextTick(() => {
    // 微任务避免触发时机过早
    emits('update:modelValue', valueHtml.value);
  });
};
// 切换选择组织人员
const handelChange = (data) => {
  if (data.type === 'click') {
    editorRef.value.restoreSelection();
  }
  repAndInsText(data.userInfo);
  handelClose();
};
const onEditorBeforeUnmount = () => {
  editorRef.value?.destroy();
  editorRef.value = null;
};
watch(
  () => props.configs,
  () => {
    if (editorRef.value) {
      if (props.configs?.readOnly) {
        editorRef.value?.disable?.();
      } else {
        editorRef.value?.enable?.();
      }
    }

    defaultConfig.editorConfig = { ...defaultConfig.editorConfig, ...props.configs?.editorConfig };
  },
  { deep: true }
);
watch(
  () => editorRef.value,
  () => {
    if (props.configs?.readOnly) {
      editorRef.value?.disable?.();
    } else {
      editorRef.value?.enable?.();
    }
  }
);
watch(
  () => valueHtml.value,
  (newVal) => {
    if (!showBox.value) {
      emits('update:modelValue', newVal);
    } else {
      updateKeyWords(start);
    }
  }
);
watch(
  () => props.modelValue,
  (newVal) => {
    valueHtml.value = newVal;
  }
);

// 关键词更新
watch(
  () => keyWords.value,
  (newVal) => {
    if (newVal) {
      const list = newVal.split('  ');
      if (list.length > 1) {
        // 连续输入两个空格关闭弹窗
        handelClose();
      }
    }
  }
);

defineExpose({
  focus,
  openFullScreen,
  closeFullScreen,
  toggleFullScreen,
  isFullScreen: () => isFullScreen.value,
  isAiFormatting: () => formatLoading.value
});
</script>
<style scoped lang="less">
@import './w-editor.less';
</style>
